import { ProctoringOption } from "@verity/proctoring-option/types";
import type { CoreFileItem } from "../../core";
import {
	PROCTORING_OPTION_TEXT,
	ProctoringOptionTextConfig,
} from "../proctoring-options/option-text/ProctoringOptionText";
import {
	CategorizeProctoringOptionsParams,
	getUniqueApplicationNamesAndIcons,
	parseExtension,
} from "./confirmationHelper";
import i18nPackage from "../../i18n/i18n";
import ExtensionUnknown from "../images/configuration/extension-unknown.svg?react";
import ApplicationUnknown from "../images/configuration/app-unknown.svg?react";

/**Main Option */
export type SummaryItem = {
	key: keyof ProctoringOption;
	text: string; // Main text
	icon?: ProctoringOptionTextConfig["icon"];
	subOptions: SummarySubOption[];
	/**If component renders a list, this is tooltip on the button that opens the list */
	listTooltip?: string;
	status?: SummaryItemStatus;
	/**If present, renders `text` as a link to this file instead of bold text */
	file?: CoreFileItem;
};

/**Gets rendered in a popup */
export type SummarySubOption = {
	key: keyof ProctoringOption;
	strategy: SummarySubOptionStrategy;
	icon?: keyof ProctoringOption | (string & Record<never, never>) | ProctoringOptionTextConfig["icon"] | null;
	subOptions?: SummarySubOption[]; // For nested suboptions
};

/**Rendered as title of popup */
type HeaderStrategy = { type: "header"; text: string };
/**Defaults to bullet text if list style is undefined */
type TextStrategy = { type: "text"; text: string; listStyle?: boolean };
/**Link */
type LinkStrategy = { type: "link"; href: string; ariaLabel: string };
/**Bypass mode.*/
type BypassStrategy = { type: "bypass" };

/**Will render a divider on top. Has no bullet point */
type SubHeaderStrategy = { type: "subHeader"; text: string };

type SummarySubOptionStrategy = HeaderStrategy | TextStrategy | LinkStrategy | BypassStrategy | SubHeaderStrategy;

// For typescript so that it can type narrow
export function isHeaderStrategy(strategy?: SummarySubOptionStrategy): strategy is HeaderStrategy {
	return strategy?.type === "header";
}

export function isLinkStrategy(strategy?: SummarySubOptionStrategy): strategy is LinkStrategy {
	return strategy?.type === "link";
}

export function isBypassStrategy(strategy?: SummarySubOptionStrategy): strategy is BypassStrategy {
	return strategy?.type === "bypass";
}

export function isTextStrategy(strategy?: SummarySubOptionStrategy): strategy is TextStrategy {
	return strategy?.type === "text";
}

export function isSubHeaderStrategy(strategy?: SummarySubOptionStrategy): strategy is SubHeaderStrategy {
	return strategy?.type === "subHeader";
}

function processVideoSubOptions(ctx: GroupContext): GetGroupConfigReturn {
	const { activeOptions, option, getStrictModeEnabled } = ctx;
	const subOptions: SummarySubOption[] = [];
	const mainLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.video");
	const mainAriaLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.additionalSettingsFor", {
		label: mainLabel,
	});
	const listHeader = i18nPackage.t("editQuizPage.configurationSummaryListTSX.additionalSettings");

	if (option["videoRecordingMode"] !== "strict" && getStrictModeEnabled("videoRecordingMode")) {
		subOptions.push({
			key: "videoRecordingMode",
			strategy: { type: "bypass" },
		});
	}
	if (activeOptions.includes("faceTrackingMode") && option.faceTrackingMode && option.faceTrackingMode !== "off") {
		const faceSubOption: SummarySubOption = {
			key: "faceTrackingMode",
			strategy: { type: "text", text: getCompactHeader("faceTrackingMode") },
		};

		subOptions.push(faceSubOption);
	}
	if (
		activeOptions.includes("objectDetectionMode") &&
		option.objectDetectionMode &&
		option.objectDetectionMode !== "off"
	) {
		subOptions.push({
			key: "objectDetectionMode",
			strategy: {
				type: "text",
				text: getCompactHeader("objectDetectionMode"),
			},
		});
	}
	if (
		activeOptions.includes("secondaryVideoRecordingMode") &&
		option.secondaryVideoRecordingMode &&
		option.secondaryVideoRecordingMode !== "off"
	) {
		subOptions.push({
			key: "secondaryVideoRecordingMode",
			strategy: { type: "text", text: getCompactHeader("secondaryVideoRecordingMode") },
		});
	}

	return { mainLabel, mainAriaLabel, listHeader, subOptions };
}

function processAudioSettings(ctx: GroupContext): GetGroupConfigReturn {
	const { activeOptions, option, getStrictModeEnabled, optionRelatedInstituteSettings } = ctx;
	const subOptions: SummarySubOption[] = [];
	const mainLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.audio");
	const mainAriaLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.additionalSettingsFor", {
		label: mainLabel,
	});
	const listHeader = i18nPackage.t("editQuizPage.configurationSummaryListTSX.additionalSettings");

	if (option.audioRecordingMode !== "strict" && getStrictModeEnabled("audioRecordingMode")) {
		subOptions.push({
			key: "audioRecordingMode",
			strategy: { type: "bypass" },
		});
	}

	if (activeOptions.includes("quietEnvironment") && option.quietEnvironment) {
		subOptions.push({
			key: "quietEnvironment",
			strategy: { type: "text", text: getCompactHeader("quietEnvironment") },
		});
	}
	if (activeOptions.includes("audioTranscription") && option.audioTranscription) {
		const transcriptSubOption: SummarySubOption = {
			key: "audioTranscription",
			strategy: {
				type: "text",
				text: getCompactHeader("audioTranscription"),
			},
			subOptions: [],
		};
		const totalKeywords =
			(option.flaggedKeywords?.length ?? 0) + (optionRelatedInstituteSettings?.flaggedKeywords?.length ?? 0);

		if (totalKeywords > 0 && activeOptions.includes("flaggedKeywords")) {
			transcriptSubOption.subOptions?.push({
				key: "flaggedKeywords",
				strategy: {
					type: "text",
					text: `${getCompactHeader("flaggedKeywords")} (${totalKeywords})`,
				},
			});
		}

		subOptions.push(transcriptSubOption);
	}

	return { mainLabel, mainAriaLabel, listHeader, subOptions };
}

function processIdVerificationSettings(ctx: GroupContext): GetGroupConfigReturn {
	const { activeOptions, option, getStrictModeEnabled } = ctx;
	const subOptions: SummarySubOption[] = [];
	const useAi = activeOptions.includes("idVerificationEnableAi") && !!option.idVerificationEnableAi;

	const mainLabel = getCompactHeader(useAi ? "idVerificationEnableAi" : "idVerification");
	const mainAriaLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.additionalSettingsFor", {
		label: mainLabel,
	});
	const listHeader = i18nPackage.t("editQuizPage.configurationSummaryListTSX.additionalSettings");

	if (useAi) {
		if (option.idVerificationMode === "normal" && getStrictModeEnabled("idVerificationMode")) {
			subOptions.push({
				key: "idVerification",
				strategy: { type: "bypass" },
			});
		}
	}

	return { mainLabel, mainAriaLabel, listHeader, subOptions };
}

function processBlockApplicationSettings(ctx: GroupContext): GetGroupConfigReturn {
	const { activeOptions, option, getStrictModeEnabled, applications } = ctx;
	const subOptions: SummarySubOption[] = [];
	const mainLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.applicationSettings");
	const mainAriaLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.applicationAriaLabel");

	if (activeOptions.includes("recordApplicationsActivity")) {
		subOptions.push({
			key: "recordApplicationsActivity",
			strategy: { type: "text", text: getCompactHeader("recordApplicationsActivity") },
		});
	}

	if (activeOptions.includes("logRunningApplications")) {
		subOptions.push({
			key: "logRunningApplications",
			strategy: { type: "text", text: getCompactHeader("logRunningApplications") },
		});
	}

	if (activeOptions.includes("applicationBlockMode")) {
		const appSubOption: SummarySubOption = {
			key: "applicationBlockMode",
			strategy: { type: "text", text: getCompactHeader("applicationBlockMode") },
			subOptions: [],
		};

		if (option["applicationBlockMode"] !== "strict" && getStrictModeEnabled("applicationBlockMode")) {
			appSubOption.subOptions?.push({
				key: "applicationBlockMode",
				strategy: { type: "bypass" },
			});
		}

		const hasBlockApplications = activeOptions.includes("blockWindowedApplications");
		const hasHideWindows = activeOptions.includes("hideOtherWindows");

		if (hasHideWindows) {
			appSubOption.subOptions?.push({
				key: "applicationBlockMode",
				strategy: {
					type: "text",
					text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.allApplicationsBlocked"),
				},
			});
		}

		subOptions.push(appSubOption);

		if (!hasHideWindows) {
			const listSource = hasBlockApplications ? option.allowedApplicationsList : option.applicationBlockList;
			const listSourceArray = Array.isArray(listSource) ? listSource : [];

			const processNames = listSourceArray.map((app) => String(app));
			const appMap = getUniqueApplicationNamesAndIcons(processNames, applications ?? []);

			const numApplications = appMap ? Object.keys(appMap).length : 0;
			const subHeader = hasBlockApplications
				? i18nPackage.t("editQuizPage.configurationSummaryListTSX.allowList", { count: numApplications })
				: i18nPackage.t("editQuizPage.configurationSummaryListTSX.blockList", { count: numApplications });

			subOptions.push({ key: "applicationBlockMode", strategy: { type: "subHeader", text: subHeader } });

			Object.entries(appMap).forEach(([name, svgIcon]) => {
				subOptions.push({
					key: "applicationBlockMode",
					strategy: { type: "text", text: name, listStyle: false },
					icon: svgIcon ?? ApplicationUnknown,
				});
			});
		}
	}

	return { subOptions, mainLabel, mainAriaLabel };
}

function processExtensionSettings(ctx: GroupContext): GetGroupConfigReturn {
	const { activeOptions, option, getStrictModeEnabled, extensions } = ctx;
	const mainLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.extensionSettings");
	const mainAriaLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.extensionAriaLabel");
	const subOptions: SummarySubOption[] = [];

	if (activeOptions.includes("logInstalledExtensions")) {
		subOptions.push({
			key: "logInstalledExtensions",
			strategy: { type: "text", text: getCompactHeader("logInstalledExtensions") },
		});
	}

	if (activeOptions.includes("extensionBlockMode")) {
		const extSubOption: SummarySubOption = {
			key: "extensionBlockMode",
			strategy: { type: "text", text: getCompactHeader("extensionBlockMode") },
			subOptions: [],
		};

		if (option["extensionBlockMode"] !== "strict" && getStrictModeEnabled("extensionBlockMode")) {
			extSubOption.subOptions?.push({
				key: "extensionBlockMode",
				strategy: { type: "bypass" },
			});
		}

		const allowedExtensionList =
			activeOptions.includes("allowedExtensionsList") && Array.isArray(option.allowedExtensionsList)
				? option.allowedExtensionsList
				: [];
		const numAllowedExtensions = allowedExtensionList.length;

		if (numAllowedExtensions <= 0) {
			const subPoint = i18nPackage.t("editQuizPage.configurationSummaryListTSX.allExtensionsBlocked");

			extSubOption.subOptions?.push({
				key: "extensionBlockMode",
				strategy: { type: "text", text: subPoint },
			});
		}

		subOptions.push(extSubOption);

		if (numAllowedExtensions > 0) {
			const subHeader = i18nPackage.t("editQuizPage.configurationSummaryListTSX.blockExtensions", {
				count: numAllowedExtensions,
			});

			subOptions.push({
				key: "extensionBlockMode",
				strategy: { type: "subHeader", text: subHeader },
			});

			allowedExtensionList.forEach((entry) => {
				const ext = parseExtension(String(entry), extensions ?? []);
				subOptions.push({
					key: "extensionBlockMode",
					strategy: { type: "text", text: ext.name, listStyle: false },
					icon: ext.iconSvg ?? ExtensionUnknown,
				});
			});
		}
	}

	return { subOptions, mainLabel, mainAriaLabel };
}

function getCompactHeader(key: keyof ProctoringOption): string {
	return PROCTORING_OPTION_TEXT[key]?.compactHeader ?? PROCTORING_OPTION_TEXT[key]?.header ?? key;
}

function processKeyboardAndClipboard(ctx: GroupContext): GetGroupConfigReturn {
	const { activeOptions, option } = ctx;
	const subOptions: SummarySubOption[] = [];
	const mainLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.input");
	const mainAriaLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.inputAriaLabel");

	if (activeOptions.includes("disableClipboardMode")) {
		const clipboardValue = option.disableClipboardMode;
		const isSystemClipboard = clipboardValue && clipboardValue === "system";

		subOptions.push({
			key: "disableClipboardMode",
			strategy: {
				type: "text",
				text: isSystemClipboard
					? i18nPackage.t("editQuizPage.configurationSummaryListTSX.systemClipboard")
					: getCompactHeader("disableClipboardMode"),
			},
		});
	}

	if (activeOptions.includes("disableRightClick")) {
		subOptions.push({
			key: "disableRightClick",
			strategy: { type: "text", text: getCompactHeader("disableRightClick") },
		});
	}

	if (activeOptions.includes("recordClipboard")) {
		subOptions.push({
			key: "recordClipboard",
			strategy: { type: "text", text: getCompactHeader("recordClipboard") },
		});
	}

	if (activeOptions.includes("recordInput")) {
		subOptions.push({
			key: "recordInput",
			strategy: { type: "text", text: getCompactHeader("recordInput") },
		});

		const keyboardBlockList = Array.isArray(option.keyboardShortcutBlockList) ? option.keyboardShortcutBlockList : [];

		if (keyboardBlockList.length > 0) {
			subOptions.push({
				key: "recordInput",
				strategy: {
					type: "subHeader",
					text: i18nPackage.t("editQuizPage.configurationStep.otherActivitiesTSX.keyboardList"),
				},
			});

			keyboardBlockList.forEach((shortcut) =>
				subOptions.push({ key: "recordInput", strategy: { type: "text", text: String(shortcut) } }),
			);
		}
	}

	return {
		subOptions,
		mainLabel,
		mainAriaLabel,
	};
}

function processTabSettings(ctx: GroupContext): GetGroupConfigReturn {
	const { activeOptions, option } = ctx;
	const subOptions: SummarySubOption[] = [];
	const mainLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.tabSettings");
	const mainAriaLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.tabSettingsAriaLabel");

	if (activeOptions.includes("closeOpenTabs")) {
		subOptions.push({
			key: "closeOpenTabs",
			strategy: { type: "text", text: getCompactHeader("closeOpenTabs") },
		});
	}

	if (activeOptions.includes("disableNewTab")) {
		subOptions.push({
			key: "disableNewTab",
			strategy: { type: "text", text: getCompactHeader("disableNewTab") },
		});
	}

	if (activeOptions.includes("recordWebTraffic")) {
		subOptions.push({
			key: "recordWebTraffic",
			strategy: { type: "text", text: getCompactHeader("recordWebTraffic") },
		});
	}

	if (activeOptions.includes("tabWhiteList")) {
		const tabList = Array.isArray(option["tabWhiteList"]) ? option["tabWhiteList"] : [];

		if (tabList.length > 0) {
			subOptions.push({
				key: "tabWhiteList",
				strategy: {
					type: "subHeader",
					text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.tabWhiteList", { count: tabList.length }),
				},
			});
			tabList.forEach((url) =>
				subOptions.push({
					key: "tabWhiteList",
					strategy: {
						type: "link",
						href: String(url),
						ariaLabel: i18nPackage.t("editQuizPage.configurationSummaryListTSX.listLinkAriaLabel", {
							url: String(url),
						}),
					},
				}),
			);
		}
	}

	return {
		subOptions,
		mainLabel,
		mainAriaLabel,
	};
}

function processDisplaySettings(ctx: GroupContext): GetGroupConfigReturn {
	const { activeOptions, option, getStrictModeEnabled } = ctx;
	const subOptions: SummarySubOption[] = [];
	const mainLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.oneScreenSettings");
	const mainAriaLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.oneScreenAriaLabel");

	if (activeOptions.includes("oneScreenEnforcementMode")) {
		const screenSubOption: SummarySubOption = {
			key: "oneScreenEnforcementMode",
			strategy: { type: "text", text: getCompactHeader("oneScreenEnforcementMode") },
			subOptions: [],
		};

		if (option["oneScreenEnforcementMode"] !== "strict" && getStrictModeEnabled("oneScreenEnforcementMode")) {
			screenSubOption.subOptions?.push({
				key: "oneScreenEnforcementMode",
				strategy: { type: "bypass" },
			});
		}

		subOptions.push(screenSubOption);
	}

	return {
		subOptions,
		mainLabel,
		mainAriaLabel,
	};
}

function processHandoffSettings(ctx: GroupContext): GetGroupConfigReturn {
	const { activeOptions, option, getStrictModeEnabled } = ctx;
	const subOptions: SummarySubOption[] = [];
	const mainLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.handoffSettings");
	const mainAriaLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.handoffAriaLabel");

	if (activeOptions.includes("restrictHandoffMode")) {
		const handoffSubOption: SummarySubOption = {
			key: "restrictHandoffMode",
			strategy: { type: "text", text: getCompactHeader("restrictHandoffMode") },
			subOptions: [],
		};

		if (option["restrictHandoffMode"] !== "strict" && getStrictModeEnabled("restrictHandoffMode")) {
			handoffSubOption.subOptions?.push({
				key: "restrictHandoffMode",
				strategy: { type: "bypass" },
			});
		}

		subOptions.push(handoffSubOption);
	}

	return {
		subOptions,
		mainLabel,
		mainAriaLabel,
	};
}

function processGeofenceSettings(ctx: GroupContext): GetGroupConfigReturn {
	const { activeOptions, option } = ctx;
	const subOptions: SummarySubOption[] = [];
	const mainLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.locationSettings");
	const mainAriaLabel = i18nPackage.t("editQuizPage.configurationSummaryListTSX.locationAriaLabel");

	if (activeOptions.includes("enableGeofence")) {
		subOptions.push({
			key: "enableGeofence",
			strategy: { type: "text", text: getCompactHeader("enableGeofence") },
		});

		const geofenceArray = Array.isArray(option.geofenceLocations) ? option.geofenceLocations : [];

		if (geofenceArray.length > 0) {
			subOptions.push({
				key: "enableGeofence",
				strategy: {
					type: "subHeader",
					text: i18nPackage.t("editQuizPage.configurationSummaryListTSX.geofenceTitle", {
						count: geofenceArray.length,
					}),
				},
			});
			geofenceArray.forEach((location) => {
				subOptions.push({
					key: "enableGeofence",
					strategy: {
						type: "text",
						text: location.name,
					},
				});
			});
		}
	}

	return {
		subOptions,
		mainLabel,
		mainAriaLabel,
	};
}

export type GroupContext = CategorizeProctoringOptionsParams & {
	activeOptions: (keyof ProctoringOption)[];
	getStrictModeEnabled: (key: keyof ProctoringOption) => boolean | undefined;
};

type GetGroupConfigReturn = {
	subOptions: SummarySubOption[];
	mainLabel: string;
	/** Will be rendered as tooltip on the button that opens the popup */
	mainAriaLabel?: string;
	/**Will default to mainLabel if not provided */
	listHeader?: string;
};

// For Additional Restrictions. Since these keys don't have a clear "parent", need to group them here
type SummaryGroupConfig = {
	key: keyof ProctoringOption;
	getConfig: (params: GroupContext) => GetGroupConfigReturn;
	triggerKeys?: (keyof ProctoringOption)[];
	/**Defaults to ProctoringOptionTextConfig[key]["icon"] */
	icon?: ProctoringOptionTextConfig["icon"];
};

export const SUMMARY_GROUPS: SummaryGroupConfig[] = [
	{
		key: "videoRecordingMode",
		triggerKeys: ["videoRecordingMode", "secondaryVideoRecordingMode", "faceTrackingMode", "objectDetectionMode"],
		getConfig: processVideoSubOptions,
	},
	{
		key: "audioRecordingMode",
		triggerKeys: ["audioRecordingMode", "quietEnvironment", "audioTranscription", "flaggedKeywords"],
		getConfig: processAudioSettings,
	},
	{
		key: "idVerification",
		triggerKeys: ["idVerification", "idVerificationEnableAi", "idVerificationMode"],
		getConfig: processIdVerificationSettings,
	},
	{
		key: "recordInput",
		triggerKeys: [
			"disableClipboardMode",
			"recordClipboard",
			"recordInput",
			"disableRightClick",
			"keyboardShortcutBlockList",
		],
		getConfig: processKeyboardAndClipboard,
	},
	{
		key: "tabWhiteList",
		triggerKeys: [
			"disableNewTab",
			"closeOpenTabs",
			"tabWhiteList",
			"recordWebTraffic",
			"excludedWebTrafficRecordingUrls",
		],
		getConfig: processTabSettings,
	},
	{
		key: "oneScreenEnforcementMode",
		triggerKeys: ["oneScreenEnforcementMode"],
		getConfig: processDisplaySettings,
	},
	{
		key: "restrictHandoffMode",
		triggerKeys: ["restrictHandoffMode"],
		getConfig: processHandoffSettings,
	},
	{
		key: "enableGeofence",
		triggerKeys: ["enableGeofence"],
		getConfig: processGeofenceSettings,
	},
	{
		key: "applicationBlockMode",
		triggerKeys: [
			"applicationBlockMode",
			"blockWindowedApplications",
			"hideOtherWindows",
			"allowedApplicationsList",
			"recordApplicationsActivity",
			"logRunningApplications",
		],
		getConfig: processBlockApplicationSettings,
	},
	{
		key: "logInstalledExtensions",
		triggerKeys: ["extensionBlockMode", "allowedExtensionsList", "logInstalledExtensions"],
		getConfig: processExtensionSettings,
	},
];

/**Need this because we are sorting the options */
type SummaryItemStatus = "list" | "monitored" | "disabled" | "enabled";

export const STATUS_SORT_WEIGHT: Record<SummaryItemStatus, number> = {
	disabled: 1,
	monitored: 2,
	enabled: 3,
	list: 4,
};

export const OPTION_STATUS_MAP: Partial<Record<keyof ProctoringOption, SummaryItemStatus>> = {
	disableDownloadFiles: "disabled",
	disableSpellcheck: "disabled",
	disablePrinting: "disabled",
	enableGuidedAccessMode: "disabled",
	disableVirtualCamera: "disabled",
	blockVirtualMachines: "disabled",
	detectSidePanel: "monitored",
	uploadScratchNotes: "enabled",
	restrictDevices: "disabled",
	enableManualEndProctoring: "enabled",
	restrictHandoffMode: "disabled",
	oneScreenEnforcementMode: "disabled",
	extensionBlockMode: "disabled",
	applicationBlockMode: "disabled",
};
